import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale"

// Maps Contentful tag IDs → public locale codes.
// Tag IDs vary across spaces: some spaces use long-form region tags
// (e.g. `localeDeDe` → "de-DE"), others use short-form language tags
// (e.g. `localeDe` → "de"). Keep both variants here so the sitemap /
// metadata layer picks up whichever tags the space actually applies.
export const mappingLocaleTagsToValues: any = {
  // english is required, so there is no tag.
  // Long-form (language + region)
  localeSvSe: "sv-SE",
  localeRuRu: "ru-RU",
  localeKoKr: "ko-KR",
  localeItIt: "it-IT",
  localeEnGb: "en-GB",
  localeNlNl: "nl-NL",
  localeZhHant: "zh-Hant",
  localeZhHans: "zh-Hans",
  localeZhTw: "zh-TW",
  localeZhCn: "zh-CN",
  localeFrFr: "fr-FR",
  localeJaJp: "ja-JP",
  localeEsEs: "es-ES",
  localeDeDe: "de-DE",
  localePtBr: "pt-BR",
  localeFiFi: "fi-FI",
  // Short-form (language only)
  localeNl: "nl",
  localeDe: "de",
  localeFr: "fr",
  localeEs: "es",
  localeIt: "it",
  localeJa: "ja",
  localeKo: "ko",
  localeRu: "ru",
  localeSv: "sv",
}
export const mappingValuesToLocaleTags: any = {
  "sv-SE": "localeSvSe",
  "ru-RU": "localeRuRu",
  "ko-KR": "localeKoKr",
  "it-IT": "localeItIt",
  "en-GB": "localeEnGb",
  "nl-NL": "localeNlNl",
  "zh-Hant": "localeZhHant",
  "zh-Hans": "localeZhHans",
  "zh-TW": "localeZhTw",
  "zh-CN": "localeZhCn",
  "fr-FR": "localeFrFr",
  "ja-JP": "localeJaJp",
  "es-ES": "localeEsEs",
  "de-DE": "localeDeDe",
  "pt-BR": "localePtBr",
  "fi-FI": "localeFiFi",
  "es-MX": "localeEsEs",
  nl: "localeNl",
  de: "localeDe",
  fr: "localeFr",
  es: "localeEs",
  it: "localeIt",
  ja: "localeJa",
  ko: "localeKo",
  ru: "localeRu",
  sv: "localeSv",
}

/** True when the RC Contentful locale represents default English content. */
export function isEnglishRcLocale(locale: string): boolean {
  return locale === "en-US" || locale === "en"
}

/** GraphQL `where` fragment excluding entries tagged `hideEnglish`. */
export function hideEnglishGraphQlFilter(locale: string): string {
  return isEnglishRcLocale(locale)
    ? `{ contentfulMetadata: { tags: { id_contains_none: [ "hideEnglish" ] } } }`
    : ""
}

export function hasHideEnglishTag(entry: {
  contentfulMetadata?: { tags?: Array<{ id?: string } | null> | null } | null
}): boolean {
  return (entry?.contentfulMetadata?.tags ?? []).some(
    tag => tag?.id === "hideEnglish",
  )
}

type SiteLanguageLike = {
  code?: string | null
  localeCode?: string | null
  contentfulCode?: string | null
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === "string" && value.length > 0
}

/** Case-insensitive match for public URL locale codes (e.g. fr-FR vs fr-fr). */
export function publicLocalesEquivalent(a: string, b: string): boolean {
  if (!a || !b) return false
  if (a.toLowerCase() === b.toLowerCase()) return true
  try {
    const la = new Intl.Locale(a)
    const lb = new Intl.Locale(b)
    if (la.language.toLowerCase() !== lb.language.toLowerCase()) return false
    const ra = la.region?.toUpperCase()
    const rb = lb.region?.toUpperCase()
    if (ra && rb) return ra === rb
    // Language-only tag values (e.g. localeFr → "fr") match regional site codes.
    return true
  } catch {
    return false
  }
}

/**
 * RC entries declare locales via Contentful tags (`localeFrFr`, etc.). Map those
 * tags to the site's configured public language codes (`fr-fr`, `nl-nl`, …) for
 * hreflang alternates and sitemap entries.
 */
export function resolveRcPublicLocalesFromTags(
  tags: Array<{ id?: string } | null> | null | undefined,
  siteLanguageItems: Array<SiteLanguageLike | null> | null | undefined,
): string[] {
  const siteCodes =
    siteLanguageItems
      ?.map(item => item?.code || item?.localeCode || item?.contentfulCode)
      .filter(isNonEmptyString) ?? []

  const tagLocaleValues = (tags ?? [])
    .map(tag => (tag?.id ? mappingLocaleTagsToValues[tag.id] : undefined))
    .filter(isNonEmptyString)

  const entryLocales =
    tagLocaleValues.length > 0
      ? [DEFAULT_PUBLIC_LANG, ...tagLocaleValues]
      : [DEFAULT_PUBLIC_LANG]

  if (siteCodes.length === 0) {
    return [DEFAULT_PUBLIC_LANG]
  }

  const matched = siteCodes.filter(code =>
    entryLocales.some(entryLoc => publicLocalesEquivalent(entryLoc, code)),
  )

  const unique = Array.from(new Set(matched))
  return unique.length > 0 ? unique : [DEFAULT_PUBLIC_LANG]
}
